// Authenticated API client (token + platform headers + native base-URL).
// The offline read-cache / write-queue this once carried was never actually
// used by the app -- every module writes via fetch()/sendRequest() -- so it was
// removed rather than left as a misleading, dead safety net. Real offline use
// is the native app's job. See OfflineBanner for the honest offline signal.
import { Capacitor } from '@capacitor/core'
import { networkStatus } from './networkStatus.js'

// Determine API base URL for native bundled builds.
// Reads from localStorage (set by ServerSetup) with env var fallback.
function getApiBaseUrl() {
  if (Capacitor.isNativePlatform()) {
    const stored = localStorage.getItem('scenelightServerUrl')
    if (stored) return stored.replace(/\/+$/, '')
    const envUrl = import.meta.env.VITE_API_URL
    if (envUrl) return envUrl.replace(/\/+$/, '')
  }
  return ''
}

class ApiClient {
  constructor() {
    this.getToken = null
    this._platformHeadersGetter = null
    this._refreshBaseUrl()
  }

  // Re-read the server URL from localStorage / env. Called on construction
  // and can be called after ServerSetup stores a new URL.
  _refreshBaseUrl() {
    this.apiBaseUrl = getApiBaseUrl()
    this.baseUrl = `${this.apiBaseUrl}/api`
    networkStatus.setApiBaseUrl(this.apiBaseUrl)
  }

  // Set the token getter function (called from AuthContext)
  setTokenGetter(fn) {
    this.getToken = fn
  }

  // Set a function that returns platform headers (called from PlatformContext)
  setPlatformHeadersGetter(fn) {
    this._platformHeadersGetter = fn
  }

  async request(endpoint, options = {}) {
    this._refreshBaseUrl()
    const url = `${this.baseUrl}${endpoint}`

    let token = null
    if (this.getToken) token = await this.getToken()

    const platformHeaders = this._platformHeadersGetter ? this._platformHeadersGetter() : {}
    const headers = {
      'Content-Type': 'application/json',
      ...platformHeaders,
      ...options.headers
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    const config = { ...options, headers }
    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body)
    }

    let response
    try {
      response = await fetch(url, config)
    } catch (err) {
      // A thrown fetch means the server was unreachable, not an HTTP error --
      // flip to offline now so the UI reflects it without waiting for the ping.
      networkStatus.markUnreachable()
      throw err
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      error.status = response.status
      error.data = errorData
      throw error
    }

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json()
    }
    return response
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' })
  }
  post(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body: data })
  }
  put(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body: data })
  }
  patch(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body: data })
  }
  delete(endpoint, data = null, options = {}) {
    const config = { ...options, method: 'DELETE' }
    if (data) config.body = data
    return this.request(endpoint, config)
  }
}

export const api = new ApiClient()
export default api
export { ApiClient }
